import React, { Suspense, useMemo } from 'react';
import { Navigate } from 'react-router-dom';

const STORAGE_KEY = 'gop_pending_registration';

// Break the App.js ↔ ContinueRegistration circular import by lazy-loading
// the two success components from the shim module.
const FreeOctoberSuccess = React.lazy(() =>
  import('./registration-success-shims').then(m => ({ default: m.FreeOctoberSuccess }))
);
const BankTransferInstructions = React.lazy(() =>
  import('./registration-success-shims').then(m => ({ default: m.BankTransferInstructions }))
);

/**
 * ContinueRegistration — trampoline reached from PreviewCertificate's primary
 * CTA.  Reads the pending registration blob from localStorage and hands it to
 * whichever post-registration success screen matches `next_step`.
 */
export default function ContinueRegistration() {
  const pending = useMemo(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  if (!pending || !pending.registration_id) {
    return <Navigate to="/register" replace />;
  }

  const reg = {
    registration_id: pending.registration_id,
    status: pending.status,
    next_step: pending.next_step,
    paid_until: pending.paid_until,
    boc_qr_image_url: pending.boc_qr_image_url,
    amount_lkr: pending.amount_lkr,
    amount_3mo_lkr: pending.amount_3mo_lkr,
  };
  const done = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    window.location.assign('/');
  };

  const Screen = pending.next_step === 'free_october'
    ? FreeOctoberSuccess
    : BankTransferInstructions;

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"
           data-testid="continue-loading">
        Loading…
      </div>
    }>
      <Screen reg={reg} onDone={done} />
    </Suspense>
  );
}
